import { find } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { VISIBLE_CANCEL_STATUS_DETAILS } from 'constants/order.constants';
import {
  AdditionalOrderColumn,
  FilterDefinitionType,
  OrdersTab,
  OrderStatus,
} from 'enums';
import { Orders } from 'features/orders';
import {
  useWhiteTriangle,
  useCurrencies,
  useOrderQueryFilters,
  useUserContext,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { FilterDefinition, OrderFilters } from 'types';
import { formUtils, orderUtils, requisitesUtils } from 'utils';

export const TraderDisputeOrdersArchive: React.FC = () => {
  const { t: tFilters } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.filters',
  });

  const { paymentTypes, banks } = useUserContext();
  const { isWhiteTriangle, requisitesId } = useWhiteTriangle();
  const { getFiatCurrencyCode, fiatCurrenciesOptions } = useCurrencies();

  useOrderQueryFilters({
    ...(isWhiteTriangle && { requisitesId }),
  });

  const additionalFilters: FilterDefinition<OrderFilters>[] = useMemo(
    () => [
      {
        label: tFilters('payment_type'),
        name: 'paymentTypeId',
        type: FilterDefinitionType.Select,
        hidden: isWhiteTriangle,
        options: requisitesUtils.getPaymentTypesOptions(paymentTypes),
        getDisplayName: (value: string) =>
          requisitesUtils.getPaymentTypeLabel(
            find(paymentTypes, { id: value })!,
          ),
      },
      {
        label: tFilters('bank'),
        name: 'bankId',
        type: FilterDefinitionType.Select,
        options: formUtils.getOptions(banks),
        hidden: isWhiteTriangle,
        getDisplayName: (value: string) => find(banks, { id: value })?.name,
      },
      {
        label: tFilters('fiat_currency'),
        name: 'fiatCurrencyId',
        type: FilterDefinitionType.Select,
        options: fiatCurrenciesOptions,
        hidden: isWhiteTriangle,
        getDisplayName: getFiatCurrencyCode,
      },
      {
        label: tFilters('cancel_reason'),
        name: 'statusDetails',
        type: FilterDefinitionType.Enum,
        enum: VISIBLE_CANCEL_STATUS_DETAILS,
        getDisplayName: orderUtils.getStatusDetailsLabel,
      },
      ...(!isWhiteTriangle
        ? requisitesUtils.getRequisitesFieldsFilters<OrderFilters>()
        : []),
      orderUtils.getStatusFilterDefinition([
        OrderStatus.Completed,
        OrderStatus.Cancelled,
      ]),
    ],
    [
      banks,
      fiatCurrenciesOptions,
      getFiatCurrencyCode,
      paymentTypes,
      tFilters,
      isWhiteTriangle,
    ],
  );

  return (
    <Orders
      tab={OrdersTab.DisputeArchive}
      additionalFilters={additionalFilters}
      additionalColumns={[
        AdditionalOrderColumn.TraderCard,
        AdditionalOrderColumn.StatusActive,
        AdditionalOrderColumn.CancelReason,
      ]}
    />
  );
};
